import { applyFilterUnion } from '@warebee/shared/athena-db-model';
import {
  FilterFieldConfigBase,
  FilterPreset,
  getKyselyFilterDescSet,
  KeysMatching,
} from '@warebee/shared/common';
import {
  SortDirection,
  StringSearchFilter,
  StringSearchFilterType,
} from '@warebee/shared/data-access-api-dto';
import { InferResult, sql } from 'kysely';
import _ from 'lodash';
import { getAnalyzedJobLineQueryBuilder } from '../../analyze/analyzedJobLineQueryBuilder';
import { getAssignmentQueryBuilder } from '../../assignment/assignmentsQueryBuilder';
import { ActualityQueryBuilderParams } from '../../datasetQueryBuilder.types';
import { getItemsQueryBuilder } from '../../itemSet/itemsQueryBuilder';
import { getLocationQueryBuilder } from '../../layoutLocations/locationsQueryBuilder';
import { orderStatsDataColumnsAll } from '../../orderSet/orderSetStatsQueryBuilder';
import { feedExtendedEventsQueryBuilder } from '../../queryBuilder/feedExtendedEventsQueryBuilder';
import { getActualityEmployeeShiftQueryBuilder } from '../cteQueryBuilders/actualityEmployeeShiftQueryBuilder';
import { getActualityExtendedEventQueryBuilder } from '../cteQueryBuilders/actualityExtendedEventQueryBuilder';

// export type ActualityHqDataRowBase = ActivityFeedDataRow &
//   Omit<
//     ActualityExtendedEventDataRow,
//     'extendedEventId' | 'extendedEventEndTime'
//   > &
//   Omit<ItemDataRow, `itemExtra${string}`> &
//   Omit<LocationDataRow, `locationExtra${string}`> &
//   // Omit<AssignmentDataRow, `asgmntExtra${string}`> &
//   ActualityEmployeeShiftDataRow &
//   OrderStatsDataRow & {
//     pickByAssignment: boolean;
//     hasIssueWithProductSequence: number;
//     hasIssueWithLocationPerProduct: number;
//     layoutWarehouseArea: string;
//     eventEndTimeMs: number;
//     handledWeight: number;
//     handledVolume: number;
//   };

const getActualityHqSub = (params: ActualityHqQueryBuilderParams) =>
  getActualityHqQueryBuilder(params).selectFrom('hq').selectAll();

export type ActualityHqDataRows = InferResult<
  ReturnType<typeof getActualityHqSub>
>;
export type ActualityHqDataRow = ActualityHqDataRows[number];

export type ActualityHqDataColumn = keyof ActualityHqDataRow;

export type ActualityHqQueryBuilderParams = ActualityQueryBuilderParams & {
  filterBy?: ActualityHqFilterBy;
  filterPresets?: FilterPreset<ActualityHqDataColumn>[];
  filterConfig?: FilterFieldConfigBase<ActualityHqDataColumn>[];
};

export type ActualityHqSortBy = {
  direction?: SortDirection;
  field: ActualityHqDataColumn;
};

export type ActualityHqFieldWithStringFilter = KeysMatching<
  ActualityHqDataRow,
  string
>;

export type ActualityHqFilterBy = Partial<
  Record<ActualityHqFieldWithStringFilter, StringSearchFilter>
>;

export function getActualityHqQueryBuilder(
  params: ActualityHqQueryBuilderParams,
) {
  let builder = getAnalyzedJobLineQueryBuilder(
    params,
    feedExtendedEventsQueryBuilder(params),
  )
    .with(
      cte => cte('is'),
      db => getItemsQueryBuilder(params.itemSet, db),
    )
    .with(
      cte => cte('l'),
      db => getLocationQueryBuilder(params.layout, db),
    )
    .with(
      cte => cte('a'),
      db => getAssignmentQueryBuilder(params.assignment, db),
    )
    .with(
      cte => cte('aee'),
      db => getActualityExtendedEventQueryBuilder(params.documentContext, db),
    )
    .with(
      cte => cte('shift'),
      db => getActualityEmployeeShiftQueryBuilder(params.documentContext, db),
    )
    // Create Hq
    .with(
      cte => cte('hqRaw'),
      db => {
        return db
          .selectFrom('af')
          .leftJoin('is', join =>
            join
              .onRef('af.consignee', '=', 'is.consignee')
              .onRef('af.sku', '=', 'is.sku')
              .onRef('af.uom', '=', 'is.uom'),
          )
          .fullJoin('l', join =>
            join.onRef('af.locationId', '=', 'l.locationId'),
          )
          .leftJoin('item_stats', join =>
            join.onRef('af.skuKey', '=', 'item_stats.skuKey'),
          )
          .leftJoin('aee', join =>
            join.on(eb =>
              eb.and([
                eb('af.eventId', '=', eb.ref('extendedEventId')),
                eb('af.eventEndTime', '=', eb.ref('extendedEventEndTime')),
              ]),
            ),
          )
          .leftJoin('shift', join =>
            join.on(eb =>
              eb.and([
                eb('af.agentUser', '=', eb.ref('shift.employeeId')),
                eb('af.eventEndTime', '>=', eb.ref('shiftStart')),
                eb('af.eventEndTime', '<=', eb.ref('shiftEnd')),
              ]),
            ),
          )
          .leftJoin('a', join =>
            join
              .onRef('af.locationId', '=', 'a.locationId')
              .onRef('af.consignee', '=', 'a.consignee')
              .onRef('af.sku', '=', 'a.sku'),
          )
          .leftJoin('ajl', join =>
            join.onRef('af.eventId', '=', 'ajl.sourceEventId'),
          )
          .selectAll(['af', 'shift'])
          .select([
            // 'consignee'
            // 'sku'
            // 'uom'
            'is.skuGroup',
            'is.lowerUom',
            'is.unitsPerLowestUom',
            'is.netWeight',
            'is.length',
            'is.width',
            'is.height',
            'is.volume',
            'is.ean',
            'is.upc',
            'is.name',
            'is.description',
            'is.subGroup',
            'is.transportClass',
            'is.stockCategory',
            'is.storageClass',
            'is.pollutionClass',
            'is.maxStack',
            'is.orientationConstraint',
          ])

          .select([
            'l.locationId as layoutLocationId',
            'locationOrder',
            'locationStatus',
            'locationLevel',
            'locationLength',
            'locationWidth',
            'locationHeight',
            'locationWeight',
            'locationBayId',
            'locationBayTitle',
            'locationBayPosition',
            'locationDepthPosition',
            'locationUsageType',
            'locmhtype',
            'locationRackingType',
            'l.warehouseArea as layoutWarehouseArea',
            'locationSide',
            'congestionZone',
            'locationBayProjection',
            'locationHeightFromFloor',
            'locationDepthFromFront',
            'locationIndexFromFront',
            'locationVolume',
            'bayType',
            'aisleId',
            'aisleTitle',
            'planeId',
            'planeTitle',
            //'shape'
            //'portals'
            'x',
            'y',
          ])
          .select([
            'previousEmployeeJobActivityTime',
            'followingEmployeeJobActivityTime',
            'previousEmployeeActivityTime',
            'followingEmployeeActivityTime',
            'previousJobActivityTime',
            'followingJobActivityTime',
            'previousJobEvent',
            'followingJobEvent',
            'previousJobEventByLocationOrder',
            'followingJobEventByLocationOrder',
            'jobLineSequence',
            'jobLineSequenceByLocationOrder',
            'jobLinesSameProduct',
            'jobLinesSameAssignment',
            'employeeActivityDuration',
            'jobLineDuration',
            'visitKey',
          ])
          .select([
            'ajl.cost',
            'ajl.duration',
            'ajl.distance',
            'ajl.durationTravellingStart',
            'ajl.distanceTravellingStart',
            'ajl.costTravellingStart',
            'ajl.durationTravellingEnd',
            'ajl.distanceTravellingEnd',
            'ajl.costTravellingEnd',
            'ajl.durationTravellingInternal',
            'ajl.distanceTravellingInternal',
            'ajl.costTravellingInternal',
            'ajl.durationTravellingVertical',
            'ajl.costTravellingVertical',
            'ajl.distanceTravellingVertical',
            'ajl.durationHandling',
            'ajl.costHandling',
            'ajl.durationPicking',
            'ajl.costPicking',
            'ajl.durationReordering',
            'ajl.costReordering',
            'ajl.route',
            'ajl.reasons',
            'ajl.ignoredEventsCount',
          ])
          .select(['a.assignmentItem'])
          .select(orderStatsDataColumnsAll)
          .select(eb => [
            sql<number>`to_unixtime(${sql.ref('af.eventEndTime')})`.as(
              'eventEndTimeMs',
            ),
            sql<number>`${sql.ref('is.netWeight')}*${sql.ref('quantity')}`.as(
              'handledWeight',
            ),
            sql<number>`${sql.ref('is.volume')}*${sql.ref('quantity')}`.as(
              'handledVolume',
            ),
            //#region hasIssueWithProductSequence
            eb
              .case()
              .when(
                eb.and([
                  eb(eb.ref('previousJobEventByLocationOrder'), 'is not', null),
                  eb(
                    eb.ref('previousJobEvent'),
                    '<>',
                    eb.ref('previousJobEventByLocationOrder'),
                  ),
                ]),
              )
              .then(1)
              .else(0)
              .end()
              .as('hasIssueWithProductSequence'),
            //#endregion

            //#region hasIssueWithLocationPerProduct
            eb
              .case()
              .when(
                eb.and([
                  eb(eb.ref('jobLinesSameAssignment'), 'is not', null),
                  eb(
                    eb.ref('jobLinesSameAssignment'),
                    '<>',
                    eb.ref('jobLinesSameProduct'),
                  ),
                ]),
              )
              .then(1)
              .else(0)
              .end()
              .as('hasIssueWithLocationPerProduct'),
            //#endregion

            eb
              .case()
              .when('a.consignee', 'is', null)
              .then(false)
              .else(true)
              .end()
              .as('pickByAssignment'),

            eb
              .case()
              .when(
                eb.and([
                  eb('previousEmployeeJobActivityTime', 'is', null),
                  eb('previousEmployeeActivityTime', '<', eb.ref('shiftStart')),
                ]),
              )
              .then(
                sql<number>`to_milliseconds(${eb.ref('eventEndTime')} - ${eb.ref('shiftStart')})`,
              )
              .else(null)
              .end()
              .as('idleDurationShiftStart'),

            eb
              .case()
              .when(
                eb.and([
                  eb('followingEmployeeJobActivityTime', 'is', null),
                  eb('followingEmployeeActivityTime', '>', eb.ref('shiftEnd')),
                ]),
              )
              .then(
                sql<number>`to_milliseconds(${eb.ref('shiftEnd')}- ${eb.ref('eventEndTime')} )`,
              )
              .else(null)
              .end()
              .as('idleDurationShiftEnd'),
            eb
              .case()
              .when(
                eb.and([
                  eb('previousEmployeeJobActivityTime', 'is', null),
                  eb(
                    'previousEmployeeActivityTime',
                    '>=',
                    eb.ref('shiftStart'),
                  ),
                ]),
              )
              .then(
                sql<number>`to_milliseconds(${eb.ref('eventEndTime')} - ${eb.ref('previousEmployeeActivityTime')} )`,
              )
              .else(null)
              .end()
              .as('idleDurationBetweenJobs'),

            sql<string>`date_trunc('hour' , ${sql.ref('eventEndTime')})`.as(
              'eventHour',
            ),
            sql<string>`date_trunc('day' , ${sql.ref('eventEndTime')})`.as(
              'eventDay',
            ),
            sql<string>`date_trunc('week' , ${sql.ref('eventEndTime')})`.as(
              'eventWeek',
            ),

            sql<string>`date_trunc('day' , ${eb.ref('shiftStart')})`.as(
              'shiftDay',
            ),
          ]);
      },
    )
    // Apply Filters
    .with(
      cte => cte('hq'),
      db => {
        let builder = db.selectFrom('hqRaw').selectAll();

        if (params.filterBy) {
          builder = _.reduce(
            params.filterBy,
            (q, value, key) => {
              if (
                value?.type === StringSearchFilterType.CONTAINS &&
                value?.value === 'undefined'
              ) {
                return q.where(key as any, 'is', null);
              }

              if (
                value?.type === StringSearchFilterType.CONTAINS &&
                !_.isEmpty(value?.value)
              ) {
                return q.where(key as any, 'like', `%${value.value}%`);
              }

              return q;
            },
            builder,
          );
        }
        _.forEach(params.filterPresets, preset => {
          if (!_.isEmpty(preset?.filterUnion?.anyOf)) {
            const [filterBuilder, exp] = applyFilterUnion(
              builder,
              getKyselyFilterDescSet(params.filterConfig),
              preset.filterUnion,
            );
            builder = filterBuilder.where(exp);
          }
        });

        return builder;
      },
    );
  return builder;
}
